"use client";

import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { supabase } from '../lib/supabase';
import { trackActivity } from '../lib/userActivity';
import { normalizeVersion } from '../config/versionAccess';

const AuthContext = createContext();

const DEFAULT_VERSION = "FREE";

const normalizeUser = (userData) => {
  if (!userData) return null;
  return {
    ...userData,
    version: normalizeVersion(userData.version),
    type: userData.type || "professional",
    hasReadProfessionalGuide: userData.user_metadata?.hasReadProfessionalGuide || false, // 🟢 Normalized
    guideReadAt: userData.user_metadata?.guideReadAt || null // 🟢 Timestamp for revalidation
  };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authReady, setAuthReady] = useState(false);
  const [profileReady, setProfileReady] = useState(false);
  const syncInProgress = useRef(false);
  // 🎯 Geração do estado de auth: incrementada a cada ocorrência do
  // subscriber (novo login, refresh de token, logout). Usada só para
  // descartar com segurança um resultado tardio de /api/auth/sync que
  // já não corresponde à sessão vigente — nunca para decidir se um
  // SIGNED_IN "pertence" a alguma tentativa específica.
  const authGenerationRef = useRef(0);
  // 🎯 Latest-wins, profundidade 1 (não é fila): guarda a sessão mais
  // recente que ainda precisa de /api/auth/sync caso um sync já esteja
  // em voo quando ela chegar. É sobrescrita (nunca acumulada) a cada
  // evento novo, e limpa quando o estado desejado passa a ser "sem
  // sessão" (SIGNED_OUT) — assim nenhuma sessão fica perdida, mas também
  // nunca processamos mais de uma pendência por vez.
  const pendingSyncRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") {
      setLoading(false);
      setAuthReady(false);
      setProfileReady(false);
      return;
    }

    // 🎯 Executa /api/auth/sync para uma (session, generation) específica,
    // sem bloquear quem a chamou. Ao terminar — sucesso, erro ou timeout —
    // libera o lock e, se uma sessão mais nova chegou nesse meio-tempo
    // (pendingSyncRef), processa-a em seguida. Isso garante "latest valid
    // auth state wins" sem manter mais de um sync em voo e sem fila
    // ilimitada: só existe uma pendência por vez, sempre a mais recente.
    const runProfileSync = (targetSession, generation) => {
      syncInProgress.current = true;

      const syncPromise = fetch('/api/auth/sync', {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${targetSession.access_token}`
        },
        body: JSON.stringify({
          name: targetSession.user.user_metadata?.name || targetSession.user.email?.split('@')[0]
        })
      });

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Sync request timed out')), 5000)
      );

      Promise.race([syncPromise, timeoutPromise])
        .then((response) => response.json())
        .then((data) => {
          if (data.success && data.user) {
            // 🎯 Só aplica se nenhuma ocorrência mais nova aconteceu
            // enquanto este sync estava em voo.
            if (generation === authGenerationRef.current) {
              // Merge database user with Supabase Auth metadata
              setUser(normalizeUser({
                ...data.user,
                user_metadata: targetSession.user.user_metadata // Preserve Auth metadata
              }));
              setProfileReady(true);
              if (process.env.NODE_ENV !== 'production') {
                console.log(`[AuthContext] profileReady: ${Date.now()}`);
              }
            } else if (process.env.NODE_ENV !== 'production') {
              console.log('[AuthContext] Sync tardio descartado (geração obsoleta).');
            }
          } else {
            throw new Error('Sync failed or returned no user');
          }
        })
        .catch((error) => {
          console.error('🔴 AuthContext: Erro/Timeout no sync:', error);
          // O sync falhou, mas a sessão Supabase continua válida.
          // O setUser(...) otimista (pré-sync) já preencheu os dados vitais na UI.
          if (generation === authGenerationRef.current) {
            setProfileReady(false);
          }
        })
        .finally(() => {
          syncInProgress.current = false;
          // 🎯 Latest-wins: se uma sessão mais nova chegou enquanto este
          // sync rodava, ela foi registrada em pendingSyncRef em vez de
          // perdida — processa-a agora, uma única vez (coalescida caso
          // várias tenham chegado nesse meio-tempo).
          const next = pendingSyncRef.current;
          pendingSyncRef.current = null;
          if (next) {
            runProfileSync(next.session, next.generation);
          }
        });
    };

    // Listen to Supabase auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      // 🛡️ PASSWORD_RECOVERY: sessão temporária só para o usuário definir nova senha.
      // NÃO deve ser tratada como login normal (não chama /api/auth/sync, não altera
      // o estado global de autenticação do app). pages/reset-password.jsx trata esse
      // evento com seu próprio listener local.
      if (event === 'PASSWORD_RECOVERY') {
        if (process.env.NODE_ENV !== 'production') {
          console.log('[AuthContext] PASSWORD_RECOVERY ignorado (tratado por pages/reset-password.jsx)');
        }
        return;
      }

      if (session?.user) {
        // 🎯 Geração desta ocorrência — só usada para descartar com
        // segurança um resultado tardio do sync abaixo, nunca para decidir
        // se publicamos session/authReady/user (isso continua imediato e
        // incondicional, como sempre foi).
        authGenerationRef.current += 1;
        const myGeneration = authGenerationRef.current;

        setSession(session);
        setAuthReady(true);
        if (process.env.NODE_ENV !== 'production') {
          console.log('[AuthContext] session available:', !!session?.access_token);
          console.log(`[AuthContext] authReady: ${Date.now()}`);
        }

        // 🏎️ Renderização Otimista: Define dados básicos imediatamente
        setUser(normalizeUser({
          id: session.user.id,
          email: session.user.email,
          name: null,
          version: DEFAULT_VERSION,
          type: 'professional',
          user_metadata: session.user.user_metadata
        }));
        // 🎯 Latest-wins: no máximo um /api/auth/sync em voo por vez. Se já
        // existe um rodando, esta sessão não é descartada — vira a
        // pendência mais recente e será processada assim que o sync atual
        // terminar (substituindo qualquer pendência anterior, nunca
        // acumulando fila).
        if (syncInProgress.current) {
          pendingSyncRef.current = { session, generation: myGeneration };
          if (process.env.NODE_ENV !== 'production') {
            console.log('[AuthContext] Sync em andamento — sessão registrada como pendência mais recente.');
          }
          return;
        }

        runProfileSync(session, myGeneration);
      } else {
        // 🎯 Logout também é uma nova geração: garante que um sync ainda
        // em voo de antes do logout não sobrescreva o estado deslogado
        // quando finalmente resolver. E o estado desejado agora é "sem
        // sessão" — qualquer pendência de sync ainda não executada deixa
        // de fazer sentido.
        authGenerationRef.current += 1;
        pendingSyncRef.current = null;

        setUser(null);
        setSession(null);
        setAuthReady(false);
        setProfileReady(false);
        setLoading(false);
        // Limpa o cookie de sessão ao deslogar
        if (typeof document !== 'undefined') {
          document.cookie = 'kc_session=; Path=/; Max-Age=0; SameSite=Lax';
        }
      }
      setLoading(false);
    });

    // Check for existing session
    supabase.auth.getSession();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const getAccessToken = () => session?.access_token || null;

  const loginUser = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;

      // Track login activity
      if (data.user) {
        try {
          await trackActivity({
            userId: data.user.id,
            activityType: 'LOGIN',
            metadata: { email: data.user.email },
          });
        } catch (err) {
          console.error('Failed to track login activity:', err);
        }
      }

      return { success: true, user: data.user };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message };
    }
  };

  const registerUser = async (email, password, userData = {}) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name: userData.name || email.split('@')[0]
          }
        }
      });

      if (error) throw error;

      // Create user profile in database
      if (data.user) {
        try {
          const { error: profileError } = await supabase
            .from('users')
            .upsert({
              id: data.user.id,
              email,
              name: userData.name || email.split('@')[0],
              type: userData.type || 'professional',
              version: normalizeVersion(userData.version)
            }, {
              onConflict: 'id'
            });

          if (profileError) {
            console.error('Profile creation error:', profileError);
          }
        } catch (err) {
          console.error('Failed to create user profile:', err);
        }
      }

      return { success: true, user: data.user };
    } catch (error) {
      console.error('Register error:', error);
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      // Track logout activity before signing out
      if (user) {
        try {
          await trackActivity({
            userId: user.id,
            activityType: 'LOGOUT',
            metadata: { email: user.email },
          });
        } catch (err) {
          console.error('Failed to track logout activity:', err);
        }
      }

      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      setUser(null);
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      return { success: false, error: error.message };
    }
  };

  const markProfessionalGuideAsRead = async () => {
    if (!user) return;
    const now = new Date().toISOString();

    const attemptUpdate = async () => {
      return await supabase.auth.updateUser({
        data: {
          hasReadProfessionalGuide: true,
          guideReadAt: now
        }
      });
    };

    try {
      let { data, error } = await attemptUpdate();

      // 🟡 Se falhar, tentar refresh da sessão e uma segunda tentativa
      if (error) {
        console.warn('[GuideGate] updateUser falhou, tentando refresh de sessão...', error.message);
        const { error: refreshError } = await supabase.auth.refreshSession();
        if (refreshError) {
          throw new Error(`Sessão inválida. Não foi possível renovar: ${refreshError.message}`);
        }
        // Segunda tentativa após refresh
        ({ data, error } = await attemptUpdate());
      }

      if (error) throw error;

      // 🟢 Log for Governance/Audit
      try {
        const token = getAccessToken();
        await fetch('/api/user/read-guide', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            version: '2026-01-18' // Should match constant in Gate
          })
        });
      } catch (logErr) {
        console.warn('Failed to log guide acceptance:', logErr);
        // Does not block the UX
      }

      // Update local state immediately for seamless UX
      setUser(prev => ({
        ...prev,
        hasReadProfessionalGuide: true,
        guideReadAt: now
      }));
      return { success: true };
    } catch (err) {
      console.error("Failed to update guide status:", err);
      return {
        success: false,
        error: err.message || 'Erro desconhecido ao salvar confirmação do guia.'
      };
    }
  };

  const value = {
    user,
    session,
    getAccessToken,
    authReady,
    profileReady,
    loading,
    loginUser,
    registerUser,
    logout,
    markProfessionalGuideAsRead // 🟢 Exposed for Gate
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
